import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import List from '@mui/material/List'
import ListSubheader from '@mui/material/ListSubheader'
import ListItem from '@mui/material/ListItem'
import Menu from '@mui/material/Menu'
import SettingsIcon from '@mui/icons-material/Settings'
import Button from '@mui/material/Button'
import RoutedDropdownSelect from '../../../components/RoutedDropdownSelect'
import { CurveType } from '@mui/x-charts/models'
import { UserMeContext } from '../../../../contexts/UserMeContext'
import {
    barSeriesDropdownItems,
    barSeriesLabelKeys,
    graphBarSerieRoutingParam,
    graphLineSerieRoutingParam,
    lineSeriesDropdownItems,
    lineSeriesLabelKeys,
} from './constants'

export const TradesGraphToolbar = ({
    defaultBarSerie,
    defaultLineSerie,
    onChangeCurveType,
    selectedCurveType,
}: {
    defaultBarSerie: (typeof barSeriesDropdownItems)[number]['id']
    defaultLineSerie: (typeof lineSeriesDropdownItems)[number]['id']
    onChangeCurveType: (curveType: CurveType) => void
    selectedCurveType: CurveType
}) => {
    const { t } = useTranslation()
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
    const handleToggleMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }
    const { updateMe } = useContext(UserMeContext)

    const handleChangeLineSerie = (serie: (typeof lineSeriesDropdownItems)[number]['id']) => {
        updateMe({ settings: { tradesGraph: { serie } } }).catch((err) => console.error(err))
    }

    const handleChangeBarSerie = (barSerie: (typeof barSeriesDropdownItems)[number]['id']) => {
        updateMe({ settings: { tradesGraph: { barSerie } } }).catch((err) => console.error(err))
    }

    const handleToggleCurveType = () => {
        const curveType = selectedCurveType === 'monotoneX' ? 'linear' : 'monotoneX'
        onChangeCurveType(curveType)
        updateMe({ settings: { tradesGraph: { curveType } } }).catch((err) => console.error(err))
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const translatedLineSeriesItems = lineSeriesDropdownItems.map((item) => ({
        ...item,
        label: t(lineSeriesLabelKeys[item.id]),
    }))

    const translatedBarSeriesItems = barSeriesDropdownItems.map((item) => ({
        ...item,
        label: t(barSeriesLabelKeys[item.id]),
    }))

    return (
        <>
            <Button
                id='settings-button'
                aria-haspopup='listbox'
                aria-controls='settings-menu'
                aria-label={t('pages.companies.graph.settings')}
                aria-expanded={anchorEl ? 'true' : undefined}
                onClick={handleToggleMenu}
                sx={{ position: 'absolute', top: -5, right: 1 }}
            >
                <SettingsIcon />
            </Button>
            <Menu
                id='settings-menu'
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                slotProps={{
                    list: {
                        'aria-labelledby': 'settings-button',
                        role: 'listbox',
                    },
                }}
            >
                <List
                    sx={{ width: '100%', minWidth: 200, maxWidth: 560, bgcolor: 'background.paper', borderRadius: 4 }}
                    subheader={<ListSubheader>{t('pages.companies.graph.settings')}</ListSubheader>}
                >
                    <ListItem>
                        <FormControl>
                            <RoutedDropdownSelect
                                isSlim
                                defaultItem={defaultLineSerie}
                                items={translatedLineSeriesItems}
                                label={t('pages.companies.graph.selectSeries')}
                                onChange={handleChangeLineSerie}
                                routingParam={graphLineSerieRoutingParam}
                            />
                        </FormControl>
                    </ListItem>
                    <ListItem>
                        <FormControl>
                            <RoutedDropdownSelect
                                isSlim
                                defaultItem={defaultBarSerie}
                                items={translatedBarSeriesItems}
                                label={t('pages.companies.graph.selectBars')}
                                onChange={handleChangeBarSerie}
                                routingParam={graphBarSerieRoutingParam}
                            />
                        </FormControl>
                    </ListItem>
                    <ListItem>
                        <FormControlLabel
                            control={
                                <Switch checked={selectedCurveType === 'monotoneX'} onChange={handleToggleCurveType} />
                            }
                            label={t('pages.companies.graph.smoothLine')}
                        />
                    </ListItem>
                </List>
            </Menu>
        </>
    )
}
